import React, { useEffect, useRef, useState } from "react";
import { withSnackbar } from "notistack";
import { IconButton, Tooltip } from "@mui/material";
import {
  DeleteOutlineOutlined as DeleteIcon,
  EventRepeatOutlined as ReturnIcon,
} from "@mui/icons-material";

import Loader from "components/Custom/Loader";
import TxtBox from "components/Custom/TxtBox";
import Btn from "components/Custom/Btn";
import ExcelGrid from "components/Custom/ExcelGrid";
import TxtRow from "components/Custom/TxtRow";
import AutoComplete from "components/Custom/AutoComplete";
import UiAccordion from "components/Custom/UiAccordion";
import UiDialog from "../../Custom/UiDialog";
import PurchaseSkeleton from "./PurchaseSkeleton";
import ItemReturn from "./ItemReturn";

import { ERRORS, LABELS, SUCCESS, TITLES, WARNINGS } from "common/lables";
import { DateFunctions } from "common//datefunctions";
import { COMMON_JS } from "common/scripts";

import { DEFAULTS, NOTIFY } from "common/config";
import { COLORS } from "common/colors";

import { TRANS_APIs } from "apihandlers/transaction";
import { PRODUCT_APIs } from "apihandlers/product";
import {ORDER_APIs} from "apihandlers/order"

const PurchaseBill = (props: any) => {
  const { setDashMenu } = props;

  const purchaseBillRef: any = useRef(null);
  const purchaseBillChildRef: any = useRef(null);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any>(null);
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [products, setProducts] = useState<any[]>([]);
  const [manageDialog, setManageDialog] = useState<boolean>(false);
  const [mandatoryResults, setMandatoryResults] = useState<any[]>([]);
  const [manageReturnDialog, setManageReturnDialog] = useState<boolean>(false);

  const purchaseCols = [
    {
      key: "",
      selectColumn: true,
    },
    {
      key: "expanded",
    },
    {
      key: "slNo",
      name: LABELS.SL_NO,
      width: "5%",
      type: "number",
      editable: false,
      sortable: true,
      searchable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      key: "orderNumber",
      type: "text",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.BILL_NO,
      width: "10%",
    },
    {
      key: "approveDate",
      type: "datetime",
      editable: true,
      sortable: true,
      searchable: true,
      name: LABELS.APPROVED_DATE,
      width: "10%",
    },
    {
      key: "orderDate",
      type: "date",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.BILL_DATE,
      width: "10%",
    },
    {
      key: "totalQty",
      type: "number",
      editable: true,
      sortable: true,
      searchable: true,
      name: LABELS.ITEMS,
      width: "10%",
    },
    {
      key: "total",
      type: "text",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.ITEM_AMOUNT,
      width: "15%",
      renderSum: true,
    },
    {
      key: "billAmount",
      type: "text",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.BILL_AMOUNT,
      width: "15%",
      renderSum: true,
    },
    {
      key: "billStatus",
      type: "text",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.BILL_STATUS,
      width: "10%",
    },
    {
      key: "billReturn",
      name: LABELS.BILL_RETURN,
      width: "10%",
      renderCell: (params: any) => (
        <div className="options">
          <Tooltip title={LABELS.RETURN} placement="bottom">
            <IconButton
              className="iconBtn"
              onClick={() => onReturn(params.row)}
            >
              <ReturnIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  const formulaRenderer = (row) => {
    console.log("Formula row ", row);
    const productPrice = products.find((productPrice) => {
      return productPrice.id === row.productNameId;
    });
    row["price"] = productPrice.price;
    let qt = Number(row["qty"] || 0);
    let price = Number(row["price"] || 0);
    let amount = Number(row["amount"] || 0);
    if (!isNaN(qt) && !isNaN(price)) {
      amount = price * qt;
    }
    row["amount"] = amount;
    return row;
  };
  const itemCols = [
    {
      key: "",
      selectColumn: "true",
    },
    {
      key: "slNo",
      type: "text",
      editable: false,
      sortable: false,
      searchable: false,
      name: LABELS.SL_NO,
      width: "5%",
    },
    {
      key: "productName",
      type: "dropdown",
      list: products,
      editable: true,
      sortable: false,
      searchable: false,
      name: LABELS.PRODUCT_NAME,
      formula: formulaRenderer,
      width: "30%",
    },
    {
      key: "price",
      type: "number",
      editable: false,
      sortable: false,
      searchable: false,
      name: LABELS.RATE,
      formula: formulaRenderer,
      width: "20%",
    },
    {
      key: "qty",
      type: "number",
      editable: true,
      sortable: false,
      searchable: false,
      name: LABELS.QTY,
      width: "20%",
      // isDisabled: (row: any) => {
      //   return row.amount <= 50;
      // },
      formula: formulaRenderer,
    },
    {
      key: "amount",
      type: "number",
      editable: false,
      sortable: false,
      searchable: false,
      name: LABELS.AMOUNT,
      formula: formulaRenderer,
      width: "15%",
    },
    {
      key: "options",
      name: LABELS.OPTIONS,
      width: "10%",
      renderCell: (params: any) => (
        <div className="options">
          <Tooltip title={LABELS.DELETE} placement="bottom">
            <IconButton
              className="iconBtn"
              onClick={() => onDelete(params.row)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (loading) {
      getProducts();
    }
  }, [loading]);

  const getProducts = () => {
    const args = {
      activeStatus: true,
    };
    PRODUCT_APIs.getProductsSku(args).then((res: any) => {
      const response: any = res.response;
      const { status } = res;
      if (status === 200) {
        let list = response.records.map((r) => {
          return {
            id: r.id,
            label: r.productName,
            price: r.price,
          };
        });
        setProducts(list);
        getRows();
      }
    });
  };

  const getRows = async () => {
    try {
      const fromDate = (
        document.getElementById("fromDate") as HTMLInputElement
      ).value.trim();
      const toDate = (
        document.getElementById("toDate") as HTMLInputElement
      ).value.trim();
      const args = {
        isSale: "false",
        isReturn: "false",
        pageNo: 1,
        pageLength: DEFAULTS.PAGE_LENGTH,
        fromDate,
        toDate,
      };
      setRows(null);
      const res: any = await ORDER_APIs.getOrders(args);
      const { status, response } = res;
      if (status === 200) {
        let list = response.records;
        for (let i = 0; i < list.length; i++) {
          list[i]["slNo"] = i + 1;
          list[i]["_type"] = "maingrid";
          list[i]["_expanded"] = false;
          let subList = list[i].items;
          let orderId = list[i].id;
          for (let j = 0; j < subList.length; j++) {
            subList[j]["slNo"] = j + 1;
          }
          list[i]["_subGrid"] = () => subGrid(subList, orderId);
          console.log("orderId", orderId);
        }
        setRows(list);
        setLoading(false);
      }
    } catch (error) {
      console.error("getOrders:", error);
    }
  };

  const subGrid = (subRows = [], orderId) => {
    return (
      <>
        <ExcelGrid
          id={"purchaseBillSubGrid"}
          childRef={purchaseBillChildRef}
          cols={itemCols}
          rows={subRows}
          isSubGrid={subRows.length !== 0 ? true : true}
          noTotalRows={true}
          getDefaultNewRow={() => getDefaultNewRow(orderId)}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "right",
            width: "95%",
          }}
        >
          <div
            style={{
              width: "35%",
              display: "flex",
              justifyContent: "right",
            }}
          >
            <Btn
              text={LABELS.SAVE}
              onClick={managePurchaseBill}
              marginRight={15}
              bgColor={COLORS.GRID_ADD_NEW}
              //  loading={isSubmit}
            />
          </div>
        </div>
      </>
    );
  };
  const managePurchaseBill = () => {
    let gridRows = purchaseBillChildRef.getRowData();
    const args = {
      rows: gridRows.map((r) => {
        return {
          orderId: r.orderId,
          orderItemId: r.orderItemId,
          productId: r.productIdId,
          sold: r.sold,
        };
      }),
    };
    console.log("gridRowsargs", args);
    return;
    ORDER_APIs.manageOrders(args).then((res) => {
      if (res.status === 200) {
        COMMON_JS.showNotify(props, NOTIFY.SUCCESS, SUCCESS.RECORD_SUCCESS);
        setSelectedRow({});
        reloadGrid();
      } else {
        COMMON_JS.showNotify(props, NOTIFY.ERROR, ERRORS.RECORD_FAILED);
      }
    });
  };

  const getDefaultNewRow = (orderId) => {
    const newRow: any = {
      id: orderId + purchaseBillChildRef.getRowData().length + 1,
      orderId: orderId,
      orderItemId: -1,
      productId: "",
      price: "",
      sold: "",
    };
    console.log("newRow", newRow);
    return newRow;
  };

  const onDelete = (row: any) => {
    purchaseBillChildRef.deleteRow(row.id);
    const args = {
      orderItemId: row.id,
    };
    TRANS_APIs.deleteOrderItem(args).then((res) => {
      if (res.status === 200) {
        COMMON_JS.showNotify(props, NOTIFY.SUCCESS, SUCCESS.RECORD_DELETE);
      } else {
        COMMON_JS.showNotify(props, NOTIFY.ERROR, ERRORS.RECORD_FAILED);
      }
    });
  };

  const onReturn = (row: any) => {
    setSelectedRow(row);
    setManageReturnDialog(true);
  };

  const reloadGrid = () => {
    setRows(null);
    getRows();
  };

  console.log("selectedRow", selectedRow);

  return (
    <React.Fragment>
      {
        <div className="pageInfo">
          <ul className="breadcrumbs">
            <li onClick={() => setDashMenu(LABELS.HOME)}>{LABELS.HOME}</li>
            <li className="dim">{" / "}</li>
            <li onClick={() => props.setDashMenu(LABELS.HOME)}>
              {LABELS.PURCHASE}
            </li>
            <li className="dim">{" / "}</li>
            <li className="active">{TITLES.PURCHASE_BILL}</li>
          </ul>
          <h4>{TITLES.PURCHASE_BILL}</h4>
        </div>
      }
      <div className=" filterBorder" style={{ width: "96%" }}>
        <UiAccordion id={"filter"} title={LABELS.FILTER}>
          <div className="flexRowAround">
            <div className="formGroup width30">
              <TxtRow
                label={LABELS.FROM_DATE}
                type={"date"}
                id="fromDate"
                defaultValue={DateFunctions.getDate(
                  "2days",
                  "from",
                  "YYYY-MM-DD"
                )}
              />
            </div>
            <div className="formGroup width30">
              <TxtRow
                label={LABELS.TO_DATE}
                type={"date"}
                id="toDate"
                defaultValue={DateFunctions.getDate(
                  "today",
                  "to",
                  "YYYY-MM-DD"
                )}
              />
            </div>
            <div className="formGroup width30">
              <div style={{ marginTop: "1.5rem" }}>
                <Btn
                  text={LABELS.LOAD}
                  onClick={getRows}
                  bgColor={COLORS.GRID_ADD_NEW}
                  // loading={isSubmit}
                />
              </div>
            </div>
          </div>
        </UiAccordion>
      </div>

      {rows ? (
        <>
          <div className="grid excelGridWrapper">
            <ExcelGrid
              id={"purchaseBill"}
              childRef={purchaseBillRef}
              height={window.innerHeight * 0.55}
              cols={purchaseCols}
              rows={rows}
              exportOption={true}
              newRowOption={false}
              exportName={LABELS.PURCHASE_BILL}
              onReload={reloadGrid}
              hideUnhide={true}
            />
          </div>
        </>
      ) : (
        <PurchaseSkeleton />
      )}

      {manageReturnDialog && (
        <UiDialog
          title={LABELS.RETURN_SUMMARY + ": "}
          open={true}
          size={"md"}
          onClose={() => {
            setManageReturnDialog(false);
          }}
        >
          <ItemReturn
            purchaseBillRef={purchaseBillRef}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            reloadGrid={reloadGrid}
            setManageReturnDialog={setManageReturnDialog}
          />
        </UiDialog>
      )}
    </React.Fragment>
  );
};

export default withSnackbar(PurchaseBill);
